'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
  product?: {
    name: string;
    slug: string;
  };
  shorten?: boolean;
};

export function Breadcrumbs({
  breadcrumb,
  className,
  homeText,
  product,
  shorten,
}: BreadcrumbsProps) {
  const pathname = usePathname();
  const [showAllBreadcrumbs, setShowAllBreadcrumbs] = useState(false);

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);
  const homeSlug = getSlugFromPage('home') ?? '/';
  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
  const hiddenItemsCount = product ? breadcrumbs.length : Math.max(breadcrumbs.length - 1, 0);
  const shouldShorten = Boolean(shorten && !showAllBreadcrumbs && hiddenItemsCount > 0);

  useEffect(() => {
    setShowAllBreadcrumbs(false);
  }, [pathname, shorten]);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <nav className={cn('w-full rounded-none bg-transparent', 'ea-breadcrumbs', className?.wrapper)}>
      <ol
        className={cn(
          'flex flex-row flex-wrap items-center space-x-1 text-[hsl(var(--primary))]',
          'ea-breadcrumbs__list',
          className?.list,
        )}
      >
        <li
          className={cn(
            {
              [cn(
                'pointer-events-none text-sm text-gray-600',
                'ea_breadcrumbs__item--active',
                className?.itemActive,
              )]: arePathsEqual(pathname, homeSlug),
            },
            'ea_breadcrumbs__item',
            className?.item,
          )}
        >
          <Link
            href={homeSlug}
            className={cn('ea-breadcrumbs__link text-sm', className?.link)}
            prefetch={false}
          >
            {homeText ?? 'Domov'}
          </Link>
        </li>
        {shouldShorten ? (
          <>
            <li className="w-auto">
              <span className={cn('text-sm text-border', className?.item)}>/ </span>
              <button
                type="button"
                onClick={() => setShowAllBreadcrumbs(true)}
                className={cn(
                  'appearance-none border-0 bg-transparent p-0 text-sm text-foreground underline-offset-2 hover:underline',
                  'ea-breadcrumbs__expand',
                  className?.link,
                )}
                aria-label="Show full breadcrumb path"
              >
                ...
              </button>
            </li>
            {(product || lastBreadcrumb) && (
              <li className="w-auto">
                <span className={cn('text-sm text-border', className?.item)}>/ </span>
                {product ? (
                  <span
                    className={cn(
                      'pointer-events-none text-sm text-foreground',
                      'ea_breadcrumbs__item--active',
                      className?.itemActive,
                    )}
                  >
                    {product.name}
                  </span>
                ) : (
                  <span
                    className={cn(
                      {
                        [cn(
                          'pointer-events-none text-sm text-foreground',
                          'ea_breadcrumbs__item--active',
                          className?.itemActive,
                        )]: arePathsEqual(pathname, lastBreadcrumb?.slug),
                      },
                      'ea_breadcrumbs__item',
                      className?.item,
                    )}
                  >
                    <Link
                      href={lastBreadcrumb?.slug ?? '#'}
                      className={cn('ea-breadcrumbs__link text-sm', className?.link)}
                      prefetch={false}
                    >
                      {lastBreadcrumb?.name}
                    </Link>
                  </span>
                )}
              </li>
            )}
          </>
        ) : (
          breadcrumbs.map(breadcrumb => (
            <li key={breadcrumb.slug} className="w-auto">
              <span className={cn('text-sm text-border', className?.item)}>/ </span>
              <span
                className={cn(
                  {
                    [cn(
                      'pointer-events-none text-sm text-foreground',
                      'ea_breadcrumbs__item--active',
                      className?.itemActive,
                    )]: arePathsEqual(pathname, breadcrumb.slug),
                  },
                  'ea_breadcrumbs__item',
                  className?.item,
                )}
              >
                <Link
                  href={breadcrumb.slug ?? '#'}
                  className={cn('ea-breadcrumbs__link text-sm', className?.link)}
                  prefetch={false}
                >
                  {breadcrumb.name}
                </Link>
              </span>
            </li>
          ))
        )}
        {!shouldShorten && product && (
          <li className="w-auto">
            <span className={cn('text-sm text-border', className?.item)}>/ </span>
            <span
              className={cn(
                'pointer-events-none text-sm text-foreground',
                'ea_breadcrumbs__item--active',
                className?.itemActive,
              )}
            >
              {product.name}
            </span>
          </li>
        )}
      </ol>
    </nav>
  );
}
